import { useState, useEffect } from 'react'

interface CountdownTime {
  days: number
  hours: number
  minutes: number
  seconds: number
  isExpired: boolean
}

const COOKIE_NAME = 'price_locked_997'

function getNextFridayMidnight(): Date {
  // Target = next Friday at 00:00 (start of Friday)
  // getDay(): Sunday=0, Monday=1, ..., Friday=5, Saturday=6
  const now = new Date()
  const target = new Date(now)
  target.setHours(0, 0, 0, 0)

  const currentDay = now.getDay()
  let daysUntilFriday = (5 - currentDay + 7) % 7
  // If it's already Friday and past midnight, target NEXT Friday (7 days out)
  if (daysUntilFriday === 0) {
    daysUntilFriday = 7
  }
  target.setDate(target.getDate() + daysUntilFriday)
  return target
}

function setCookie(name: string, value: string, days: number) {
  const date = new Date()
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`
}

function getCookie(name: string): string | null {
  const nameEQ = name + '='
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i].trim()
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length)
  }
  return null
}

export function useDeadlineCountdown(): CountdownTime {
  const [timeLeft, setTimeLeft] = useState<CountdownTime>(() => {
    // Check cookie immediately on mount — if locked, start as expired
    if (typeof document !== 'undefined' && getCookie(COOKIE_NAME)) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true }
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: false }
  })

  useEffect(() => {
    // If already expired by cookie, lock and stop
    if (getCookie(COOKIE_NAME)) {
      setTimeLeft({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isExpired: true,
      })
      return
    }

    const target = getNextFridayMidnight()

    const update = () => {
      const now = new Date()
      const diff = target.getTime() - now.getTime()

      if (diff <= 0) {
        // Persist the locked state in browser for 30 days
        setCookie(COOKIE_NAME, '1', 30)
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        })
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      )
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft({ days, hours, minutes, seconds, isExpired: false })
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [])

  return timeLeft
}
